import type {
  AddVehicleRequestDocumentInput,
  ApproveVehicleRequestShiftInput,
  CreateRequestRelocationBody,
  DayBatchApplyBody,
  IssueRequestEsm2Body,
  PlanVehicleRequestDayBody,
  RequestRouteTimeDto,
  RequestWaybillDto,
  RouteTripFields,
  SaveVehicleRequestShiftBody,
  VehicleRequestDayBatchResultDto,
  VehicleRequestDaysDto,
  VehicleRequestDocumentDto,
  VehicleRequestDto,
  VehicleRequestShiftsDto,
  VehicleRouteDto,
  WaybillFormCode,
} from '@technic/contracts';
import { apiFetch } from '@shared/api';

/**
 * Чем заказ живёт, пока идёт: рейс дня и перегоны, бумага, которой рейс сопровождается, смены с их
 * часами и дни линейного заказа.
 *
 * Это одна цепочка, а не четыре темы, и потому один модуль: день заказа ставят в рейс, рейс
 * печатает задание и лист, отработанное по дню записывают сменой и подписывают ею же. Разложенные
 * по предметам, эти ручки всё равно понадобились бы экрану вместе, а вопрос «куда едет этот день»
 * пришлось бы задавать в двух местах — и два ответа на него разошлись бы.
 *
 * Рукопожатий, какие есть у дверей, здесь нет: день и дата — часть адреса, а не тела, и второй
 * ответ на «за какой это день» разошёлся бы с первым. Исключение одно — `issueEsm2`: он жжёт номер
 * строгой отчётности и потому ходит с версией заявки, а выписка задним числом — ещё и с причиной и
 * ключом операции.
 *
 * Бумага соседом не тянется: `waybills` и `issueEsm2` ходят по адресам листов, но отвечают
 * контрактным `RequestWaybillDto` и заявкой целиком, а `@entities/waybill` — сосед по слою,
 * которого границы слайсу запрещают.
 */
export const vehicleRequestExecution = {
  /**
   * Что портал знает о рейсе до перевода заявки в работу: ведётся ли он, на какую дату, какие
   * рейсы на неё уже заведены и чем были заполнены графы шапки в прошлый раз. Форма либо кладёт
   * заявку в готовый рейс, либо заводит новый.
   *
   * Без `vehicleId` подсказываются рейсы того же типа ТС, что заказан в заявке: день планируют с
   * вопроса «каким рейсом заявка поедет», а машину задаёт сам рейс. С машиной — её рейсы и графы
   * шапки от её прошлого рейса (наследовать их без машины неоткуда).
   *
   * `date` передаётся, когда подачу правят прямо в форме: рейс печатает задание на день, и
   * подсказка обязана относиться к тому дню, который уедет на сервер.
   */
  routePrefill: (id: string, params: { vehicleId?: string; date?: string } = {}) =>
    apiFetch<{
      required: boolean;
      /** Бланк, по которому пойдёт лист; `null` — рейс этой заявке не ведётся. */
      formCode: WaybillFormCode | null;
      formLabel: string | null;
      reason: string | null;
      tripDate: string;
      routes: VehicleRouteDto[];
      trip: RouteTripFields | null;
    }>(`/vehicle-requests/${id}/route-prefill`, { query: { ...params } }),
  /**
   * Листы, выписанные по заявке (ADR 0041) — их печатают из карточки, не уходя в журнал. Пусто
   * там, где листов нет: аренда, заявка не в работе, тип без бланка.
   *
   * Список, а не один: у грузоперевозки лист по-прежнему один — рейс один, — а у заказа техники
   * на объект их столько, сколько недель в сроке (ЭСМ-2, миграция 0087).
   */
  waybills: (id: string) => apiFetch<RequestWaybillDto[]>(`/vehicle-requests/${id}/waybills`),
  /**
   * Выписать недельный ЭСМ-2 по требованию (ADR 0100 решение 6).
   *
   * Эта дверь — про линейный заказ. Обычному заказу на объект недели бланка выписывает сверка
   * сама, вслед за переводом в работу и правками срока (`docs/adr/0060-esm2-weekly-waybill.md`);
   * у линейного типа её режим — `on_demand`, и набор недель она берёт не из срока заявки, а из
   * того, что человек уже попросил здесь. Новую неделю линейному заказу называет только человек и
   * только отсюда — вот что у двери единственное; выписанное дальше ведёт всё равно сверка:
   * сменят машину — переоформит, сократят срок — подрежет, отменят заявку — аннулирует.
   *
   * Перечень дверей, которыми путевой лист вообще рождается, — в шапке `waybillsApi`
   * (`@entities/waybill`): их три, и эта из них одна.
   *
   * `weekOf` — любой день нужной недели: границы листа считает сервер, пересекая календарную
   * неделю со сроком заявки, — тем же правилом, каким режет срок автоматическая выписка. Машина и
   * машинист приходят выбранными: за неделю на объекте могли отработать две единицы, а водитель у
   * каждого дня свой. В ответе — заявка целиком: у неё меняется версия, и список листов вместе с
   * ней.
   */
  issueEsm2: (id: string, body: IssueRequestEsm2Body) =>
    apiFetch<VehicleRequestDto>(`/vehicle-requests/${id}/esm2`, { method: 'POST', body }),
  /**
   * Перегоны заявки: доставка техники на объект и вывоз с него. Пусто — их не заводили: технику
   * могли привезти тралом, и тогда листа на перегон не бывает вовсе.
   */
  relocations: (id: string) => apiFetch<VehicleRouteDto[]>(`/vehicle-requests/${id}/relocations`),
  createRelocation: (id: string, body: CreateRequestRelocationBody) =>
    apiFetch<VehicleRouteDto>(`/vehicle-requests/${id}/relocations`, { method: 'POST', body }),
  /**
   * Смены заказа спецтехники: дни заказа целиком, включая незаполненные. День среза (`onDate`)
   * считает сервер — по нему таблица решает, какая строка ещё в будущем и потому неактивна.
   */
  shifts: (id: string) => apiFetch<VehicleRequestShiftsDto>(`/vehicle-requests/${id}/shifts`),
  /**
   * The actual time of a freight request (readings-shifts-merge plan §8): its stops as the driver
   * recorded them and its share of the route time (D6), with the reasons it is missing. The card
   * shows it, the completion window proposes it rounded up to a whole hour (D12).
   */
  routeTime: (id: string) => apiFetch<RequestRouteTimeDto>(`/vehicle-requests/${id}/route-time`),
  /** Записать смену дня: время, машиночасы, заправку и комментарий. */
  saveShift: (id: string, date: string, body: SaveVehicleRequestShiftBody) =>
    apiFetch<VehicleRequestShiftsDto>(`/vehicle-requests/${id}/shifts/${date}`, {
      method: 'PUT',
      body,
    }),
  /** Убрать ошибочно заведённый день — пока он не подтверждён. */
  deleteShift: (id: string, date: string) =>
    apiFetch<VehicleRequestShiftsDto>(`/vehicle-requests/${id}/shifts/${date}`, {
      method: 'DELETE',
    }),
  /**
   * The object's sign-off of a work day and its revocation — one route, like the request approval.
   * On a driver day the body names the driver numbers the person saw (`expectedDriverFingerprint`).
   */
  approveShift: (id: string, date: string, body: ApproveVehicleRequestShiftInput) =>
    apiFetch<VehicleRequestShiftsDto>(`/vehicle-requests/${id}/shifts/${date}/approval`, {
      method: 'POST',
      body,
    }),
  /**
   * Дни линейного заказа (ADR 0100): дни срока целиком — с рейсом дня, его машиной, водителем,
   * листом и часами смены. День среза (`onDate`) считает сервер, как и у смен.
   *
   * `blocker` приходит вместе с таблицей, а не вместо неё: у арендного заказа дней не бывает
   * вовсе, и блок обязан объяснить это словами — теми же, которыми откажет ручка планирования.
   */
  days: (id: string) => apiFetch<VehicleRequestDaysDto>(`/vehicle-requests/${id}/days`),
  /**
   * Поставить день заказа в рейс: в уже заведённый рейс машины на этот день (`routeId`) либо в
   * новый, заводимый тут же (`newRoute`). Ровно одно из двух — «и то, и другое» означало бы два
   * разных ответа на вопрос, куда едет день.
   *
   * День — часть адреса, а не тела: второй ответ на «за какой это день» разошёлся бы с первым,
   * тем же порядком устроены смены. В ответе — таблица дней целиком: изменился не только этот
   * день, но и перечень свободных рейсов у соседних.
   */
  planDay: (id: string, date: string, body: PlanVehicleRequestDayBody) =>
    apiFetch<VehicleRequestDaysDto>(`/vehicle-requests/${id}/days/${date}/route`, {
      method: 'POST',
      body,
    }),
  /**
   * Пачка «4-П на весь период» (ADR 0207): один заход проходит срок заказа подряд — на каждый его
   * день находит или заводит рейс и выписывает по нему лист.
   *
   * Дверь **одна** на оба места, откуда её зовут, — галочку окна принятия в работу и кнопку
   * таблицы дней: правил у пачки своих нет ни одного, она повторяет подённую дверь день за днём, и
   * вторая дверь означала бы второй набор правил, расходящийся с первым молча.
   *
   * Машины в теле нет намеренно (решение 5): её пачка берёт из назначения заявки. Свободный выбор
   * развёл бы бумагу по двум машинам так, что ни гараж, ни срез «На объекте», ни ЭСМ-2 этого не
   * показали бы; нужна другая единица на отдельный день — её ставят подённой дверью.
   *
   * День, который дверь не пускает, пачку не роняет, а уходит строкой отчёта с причиной
   * (решение 7): ответ — таблица дней целиком **и** построчный отчёт. Таблица приезжает готовой,
   * чтобы карточка показала новую картину сразу: сходив за ней вторым запросом, она рискует
   * показать уже не ту, по которой составлен отчёт.
   */
  planDayBatch: (id: string, body: DayBatchApplyBody) =>
    apiFetch<VehicleRequestDayBatchResultDto>(`/vehicle-requests/${id}/days/batch`, {
      method: 'POST',
      body,
    }),
  /**
   * Снять день с рейса. Сам рейс остаётся: он мог собираться из нескольких заявок, и пустой
   * маршрут диспетчер убирает своим действием.
   */
  unplanDay: (id: string, date: string) =>
    apiFetch<VehicleRequestDaysDto>(`/vehicle-requests/${id}/days/${date}/route`, {
      method: 'DELETE',
    }),
  /**
   * The card's documents table (rental closing plan, D8): the requester's attachments and the typed,
   * dated paper in one list. Both writes answer with the table whole, so the card shows what the
   * server holds without a second request.
   */
  documents: (id: string) =>
    apiFetch<{ items: VehicleRequestDocumentDto[] }>(`/vehicle-requests/${id}/documents`),
  addDocument: (id: string, body: AddVehicleRequestDocumentInput) =>
    apiFetch<{ items: VehicleRequestDocumentDto[] }>(`/vehicle-requests/${id}/documents`, {
      method: 'POST',
      body,
    }),
  removeDocument: (id: string, fileId: string) =>
    apiFetch<{ items: VehicleRequestDocumentDto[] }>(
      `/vehicle-requests/${id}/documents/${fileId}`,
      { method: 'DELETE' },
    ),
};
